import { useState, useEffect, useCallback, useRef } from "react";
import { createPublicClient, getPlaylist } from "@seeen/api";
import type { PlaylistItem, PlaylistVideoItem, PlaylistMomentItem, Playlist } from "@seeen/api";
import type { VideoItem } from "./types";

const HLS_STANDARD = "APPLE_HLS";

function pickSrc(
  videoStreams: Array<{ standard: string; manifestFileUrl: string }>,
  videoFileUrl: string | null,
): string {
  const hls = videoStreams.find((s) => s.standard === HLS_STANDARD);
  return hls?.manifestFileUrl ?? videoFileUrl ?? "";
}

function mapVideoItem(item: PlaylistVideoItem): VideoItem {
  return {
    id: item.id,
    title: item.title,
    src: pickSrc(item.videoStreams, item.videoFileUrl),
    poster: item.thumbnailUrl ?? undefined,
    duration: item.duration ?? undefined,
    aspectRatio: item.aspectRatio ?? undefined,
    endCard: item.endCard,
    ctaTimestamps: item.ctaTimestamps,
  };
}

function mapMomentItem(item: PlaylistMomentItem): VideoItem {
  return {
    id: item.id,
    title: item.title,
    src: pickSrc(item.video.videoStreams, item.video.videoFileUrl),
    poster: item.thumbnailUrl ?? undefined,
    duration: item.duration ?? undefined,
    startTime: item.startTimestamp / 1000,
    aspectRatio: item.video.aspectRatio ?? undefined,
    endCard: item.endCard,
    ctaTimestamps: item.video.ctaTimestamps,
  };
}

export function mapPlaylistItem(item: PlaylistItem): VideoItem {
  if (item.__typename === "PublicVideoType") return mapVideoItem(item);
  return mapMomentItem(item);
}

export function mapPlaylistToVideoItems(playlist: Playlist): VideoItem[] {
  return (playlist.items.nodes ?? []).map(mapPlaylistItem);
}

const DEFAULT_PAGE_SIZE = 30;

/** Returns `max` if it is a positive number, otherwise `undefined` (no limit). */
export function normalizeMaxVideos(max: number | undefined): number | undefined {
  return max != null && Number.isFinite(max) && max > 0 ? Math.floor(max) : undefined;
}

/** Caps `videos` at `maxVideos`. Returns the input unchanged when there is no limit. */
export function limitVideos<T>(videos: T[], maxVideos: number | undefined): T[] {
  const max = normalizeMaxVideos(maxVideos);
  return max != null && videos.length > max ? videos.slice(0, max) : videos;
}

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; videos: VideoItem[]; hasNextPage: boolean }
  | { status: "error"; error: Error };

export type UseApiPlaylistResult = State & {
  /** True while a subsequent page is being fetched via `loadMore`. */
  isLoadingMore: boolean;
  /** Fetches and appends the next page. No-op if already loading or there is no next page. */
  loadMore: () => void;
};

export function useApiPlaylist(
  apiKey: string | null,
  playlistId: string | null,
  pageSize: number = DEFAULT_PAGE_SIZE,
  maxVideos?: number,
): UseApiPlaylistResult {
  const max = normalizeMaxVideos(maxVideos);
  // Never fetch more per page than the limit. The page size stays constant across
  // pages so page offsets line up.
  const requestPageSize = max != null ? Math.min(pageSize, max) : pageSize;

  const [state, setState] = useState<State>({
    status: apiKey && playlistId ? "loading" : "idle",
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageRef = useRef(1);

  useEffect(() => {
    if (!apiKey || !playlistId) return;
    setState({ status: "loading" });
    pageRef.current = 1;
    const client = createPublicClient({ apiKey });
    const playerLocationUrl =
      typeof window !== "undefined" ? window.location.href : "";
    getPlaylist(client, playlistId, {
      playerLocationUrl,
      page: 1,
      pageSize: requestPageSize,
    })
      .then((playlist) => {
        if (!playlist) {
          setState({ status: "error", error: new Error("Playlist not found") });
          return;
        }
        const videos = limitVideos(mapPlaylistToVideoItems(playlist), max);
        setState({
          status: "success",
          videos,
          hasNextPage:
            (playlist.items.pageInfo?.hasNextPage ?? false) &&
            (max == null || videos.length < max),
        });
      })
      .catch((error) =>
        setState({
          status: "error",
          error: error instanceof Error ? error : new Error(String(error)),
        }),
      );
  }, [apiKey, playlistId, requestPageSize, max]);

  const loadMore = useCallback(() => {
    if (!apiKey || !playlistId) return;
    if (state.status !== "success" || !state.hasNextPage || isLoadingMore) return;

    const nextPage = pageRef.current + 1;
    setIsLoadingMore(true);
    const client = createPublicClient({ apiKey });
    const playerLocationUrl =
      typeof window !== "undefined" ? window.location.href : "";
    getPlaylist(client, playlistId, {
      playerLocationUrl,
      page: nextPage,
      pageSize: requestPageSize,
    })
      .then((playlist) => {
        if (!playlist) return;
        pageRef.current = nextPage;
        const newVideos = mapPlaylistToVideoItems(playlist);
        setState((prev) => {
          if (prev.status !== "success") return prev;
          const videos = limitVideos([...prev.videos, ...newVideos], max);
          return {
            status: "success",
            videos,
            hasNextPage:
              (playlist.items.pageInfo?.hasNextPage ?? false) &&
              (max == null || videos.length < max),
          };
        });
      })
      .finally(() => setIsLoadingMore(false));
  }, [apiKey, playlistId, requestPageSize, max, state, isLoadingMore]);

  return { ...state, isLoadingMore, loadMore };
}
